/* Common Imports */

import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	PropertyInterface,
	PropertyMediaImage,
	PropertyMediaVideo,
	PropertyMediaVideoMetadata,
} from "../../../../../redux-magic/sub-interfaces/property-interface";

/* Component Imports */

import { Alert, Skeleton, Snackbar, styled, SvgIcon, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { BusinessProfileInterface, UserInterface } from "../../../../../models";
import ViewPropertyImagesModal from "../../../../modals/PropertyModal/ViewPropertyImagesModal";

/* Icon Imports */

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { useRouter } from "next/router";
import { createLoginFlow, LoginFlowTriggers } from "../../../../../lib/auth/helpers";
import RequestImagesIcon from "../../../../../public/images/icons/singleproperty/request_image.svg";
import { StoreLeadsThunk } from "../../../../../redux-magic/thunks";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
interface MainContainerProps {
	height: string;
}

/* Stylings */

const MainContainer = styled("div")<MainContainerProps>(({ theme, height }) => ({
	overflow: "hidden",
	display: "flex",
	flexDirection: "column",
	height: height,
	minWidth: "100%!important",
}));

const ImageSection = styled("img")(({ theme }) => ({
	height: "100%",
	width: "100%",
	// borderRadius: "16px 16px 0px 0px",
	objectFit: "contain",
	backgroundColor: "rgba(216, 216, 216, 0.5)",
}));

const LoadingContainer = styled("div")(({ theme }) => ({
	height: "30rem",
	width: "100%",
	[theme.breakpoints.down("sm")]: {
		height: "15rem",
	},
}));

const CarouselComp = styled(Carousel)(({ theme }) => ({
	width: "100%",
	"& .carousel .control-dots .dot": {
		backgroundColor: "#FFFFFF",
		opacity: 1,
	},
	"& .carousel .control-arrow, .carousel.carousel-slider .control-arrow": {
		margin: "0.5rem",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		top: "45%",
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		borderRadius: "5rem",
		opacity: 1,
		height: "2.5rem",
		width: "2.5rem",
	},
	"& .carousel .control-dots .dot.selected": {
		backgroundColor: theme.palette.primary.main,
	},
	"& .carousel .slider-wrapper": {
		borderRadius: "16px 16px 0px 0px",
	},
}));

const SeemoreContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	bottom: "2px",
	right: "0px",
	zIndex: 2,
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	padding: "5px 10px",
	// borderRadius: "1rem 0rem 0rem 0rem",
	// border: "1px solid #FFFFFF",
	cursor: "pointer",
	gap: "0.5rem",
	"&:hover": {
		opacity: 0.9,
	},
}));

const BackArrow = styled(ArrowBackIosNewIcon)(({ theme }) => ({
	position: "absolute",
	width: "40px",
	height: "40px",
	zIndex: 1,
	top: "45%",
	left: "10px",
	color: "#FFFFFF",
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	borderRadius: "50%",
	fontSize: "small",
	padding: "0.5rem",
	[theme.breakpoints.down("lg")]: {
		display: "none",
	},
}));

const ForwardArrow = styled(ArrowForwardIosIcon)(({ theme }) => ({
	position: "absolute",
	width: "40px",
	height: "40px",
	zIndex: 1,
	top: "45%",
	right: "10px",
	color: "#FFFFFF",
	backgroundColor: "rgba(0, 0, 0, 0.5)",
	borderRadius: "50%",
	fontSize: "small",
	padding: "0.5rem",
	[theme.breakpoints.down("lg")]: {
		display: "none",
	},
}));

/* Property images interface */

type Category = "all" | "exterior" | "interior" | "master_plan" | "amenities" | "route_map";

interface property_images {
	_id: ObjectId | string;
	file_id: ObjectId | string;
	url?: PropertyMediaImage | PropertyMediaVideo | string;
	video_metadata?: PropertyMediaVideoMetadata;
	mime_type?: string;
	category: Category;
	caption: string;
	priority: number;
	original_file_server_path: string;
}

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const NewPropertyImageComponent = ({
	images,
	propertyCreatedBy,
	loading,
	showContactDetails,
	session,
	propertyTitle,
	propertySlug,
	dispatch,
	isPropertyPublished,
	isEditor,
	profile_context,
	transaction_type,
	is_user_creator,
	boost_information,
	ad_text,
	propertyId,
	propertyLocation,
	login_flow_global_state,
}: {
	images: StateInterface["single_property_page"]["property"]["images"];
	propertyCreatedBy: LocalPropertyInterface["created_by"];
	showContactDetails: boolean;
	loading: boolean;
	session: StateInterface["redux_session"];
	propertyTitle: string;
	propertySlug: string;
	dispatch: Function;
	isPropertyPublished: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	transaction_type: string;
	is_user_creator: boolean;
	boost_information: StateInterface["single_property_page"]["property"]["boost_information"];
	ad_text: string;
	propertyId: string | ObjectId;
	propertyLocation: string;
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const [open, setOpen] = React.useState<boolean>(false);

	const mainContainerRef = React.useRef<HTMLDivElement>(null);

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	// lets create a state to store the width of the container
	const [containerWidth, setContainerWidth] = React.useState(0);

	// lets create a state to store the height of the container
	const [containerHeight, setContainerHeight] = React.useState(0);

	const [snackopen, setSnackOpen] = React.useState<boolean>(false);

	const [requestimagesnackbar, setRequestImageSnackbar] = React.useState<boolean>(false);

	const [allImages, setAllImages] = React.useState(
		images.map((image: property_images) => ({
			...image,
			url: typeof image.url === "object" && "regular" in image.url ? image.url.regular : image.url,
		})),
	);

	//TODO: order images somewhere else
	// Lety's get only the image with priority 0 if not fount then lets add rthe first image of the array
	const categoryOrder: Record<Category, number> = {
		all: 0,
		interior: 1,
		exterior: 2,
		amenities: 3,
		master_plan: 4,
		route_map: 5,
	};

	allImages.sort((a, b) => {
		if (a.priority === 0) return -1;
		if (b.priority === 0) return 1;
		return categoryOrder[a.category] - categoryOrder[b.category];
	});

	const allImageUrl: Array<string> = allImages.map((image: property_images) => image.url as string);

	const localLang = {
		seeAllImages:
			router.locale === "kn-IN"
				? "ಎಲ್ಲಾ ಚಿತ್ರಗಳನ್ನು ನೋಡಿ"
				: router.locale === "ar-AE"
					? "رؤية جميع الصور"
					: "See all images",
		wantToseeMoreImages:
			router.locale === "kn-IN"
				? "ಹೆಚ್ಚಿನ ಚಿತ್ರಗಳನ್ನು ನೋಡಲು ಬಯಸುತ್ತೀರಾ?"
				: router.locale === "ar-AE"
					? "هل تريد رؤية المزيد من الصور؟"
					: "Want to see more images?",
		requestImages:
			router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ವಿನಂತಿಸಿ" : router.locale === "ar-AE" ? "طلب صور" : "Request Images",
	};

	// now lets just store all the image urls with its category

	const allImageUrlsWithCategory: Record<Category, string[]> = {
		all: [],
		exterior: [],
		interior: [],
		amenities: [],
		master_plan: [],
		route_map: [],
	};

	if (allImages.length !== 0) {
		allImages.forEach((image: property_images) => {
			if (allImageUrlsWithCategory[image.category]) {
				allImageUrlsWithCategory[image.category].push(image.url as string);
			}
		});
	} else {
		Object.keys(allImageUrlsWithCategory).forEach((key) => {
			allImageUrlsWithCategory[key as Category] = [];
		});
	}

	if (loading) {
		return (
			<LoadingContainer>
				<MainContainer
					ref={mainContainerRef}
					height={`${containerHeight}px`}
				>
					<Skeleton
						variant="rectangular"
						animation="wave"
						sx={{
							width: "100%",
							height: "100%",
							borderRadius: "16px",
						}}
					/>
				</MainContainer>
			</LoadingContainer>
		);
	}

	const handleModalOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleCloseSnackbar = (event: any, reason: any) => {
		if (reason === "clickaway") {
			return;
		}
		setRequestImageSnackbar(false);
	};

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	const submit = async (call_now: boolean) => {
		call_now ? null : setSnackOpen(true);
		const first_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.first_name : "") : "") : "";
		const last_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.last_name : "") : "") : "";
		// The enquiry is from the acting profile: the business's title when acting as one, else the person's name (19 Oct 2026).
		const acting_as_business: boolean = profile_context?.is_business_profile === true;
		const enquirer_name: string = acting_as_business
			? profile_context?.page_title || ""
			: `${first_name || ""} ${last_name || ""}`.trim();

		await dispatch(
			StoreLeadsThunk({
				name: enquirer_name,
				phone: {
					country_code: session
						? session.user
							? session.user.dbUser
								? session.user.dbUser.country_code
								: "+91"
							: "+91"
						: "+91",
					phone_number: session ? (session.user ? (session.user.dbUser ? session.user.dbUser.phone : "") : "") : "",
				},
				agreed_to_tc: true,
				source: "/properties/" + propertySlug,
				listing_slug: propertySlug,
				listing_type: transaction_type == "ofb" ? "ofb property" : "property",
				listing_id: propertyId,
				is_guest: session ? false : true,
				listing_title: propertyTitle,
				listing_owner: {
					is_business_profile: propertyCreatedBy.is_business_profile,
					user_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.user_id as string | ObjectId)
							: propertyCreatedBy.user_id !== ""
								? (propertyCreatedBy.user_id as UserInterface)._id
								: undefined
						: undefined,
					business_profile_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.business_profile_id as BusinessProfileInterface)._id
							: (propertyCreatedBy.business_profile_id as string | ObjectId)
						: undefined,
				},
				user_id: session ? (session.user ? (session.user.dbUser ? session.user.dbUser._id : "") : "") : "",
				// The enquiry is from the acting profile, which the new-lead notification names (19 Oct 2026).
				is_business_profile: acting_as_business,
				business_profile_id: acting_as_business ? (profile_context.business_profile_id as string | ObjectId) : undefined,
				is_lead_from_boost: boost_information ? boost_information.active : false,
				created_by: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id as string | ObjectId,
					business_profile_id: profile_context.business_profile_id as string | ObjectId,
				},
				reminders: [
					
				],
				assigned_to: [],
				notes: "",
				last_enquiry_at: " ",
				status: "",
			}),
		);
		setRequestImageSnackbar(true);
	};

	React.useEffect(() => {
		setAllImages(
			images.map((image: property_images) => ({
				...image,
				url: typeof image.url === "object" && "regular" in image.url ? image.url.regular : image.url,
			})),
		);
	}, [images]);

	React.useEffect(() => {
		if (mainContainerRef.current) {
			setContainerWidth(mainContainerRef.current.offsetWidth);
			const width = mainContainerRef.current.offsetWidth;
			setContainerWidth(width);
			setContainerHeight((width / 16) * 9); // Calculate height based on 16:9 aspect ratio
		}
	}, [mainContainerRef.current?.offsetWidth, images]);

	return (
		<React.Fragment>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={requestimagesnackbar}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
			>
				<Alert
					onClose={(e) => {
						handleCloseSnackbar(e, "");
					}}
					severity="success"
					variant="filled"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					Request for more images has been sent successfully!
				</Alert>
			</Snackbar>
			<ViewPropertyImagesModal
				open={open}
				allImages={allImageUrl}
				allImageUrlsWithCategory={allImageUrlsWithCategory}
				handleClose={handleClose}
				showContactDetails={showContactDetails}
				propertyCreatedBy={propertyCreatedBy as LocalPropertyInterface["created_by"]}
				session={session}
				propertyTitle={propertyTitle}
				propertySlug={propertySlug}
				dispatch={dispatch}
				isPropertyPublished={isPropertyPublished}
				isEditor={isEditor}
				profile_context={profile_context}
				boost_information={boost_information}
				propertyId={propertyId}
				propertyLocation={propertyLocation}
				login_flow_global_state={login_flow_global_state}
			/>

			<div
				className="carousel-wrapper"
				style={{ position: "relative", width: "100%" }}
			>
				<CarouselComp
					autoPlay={smDevice ? true : false}
					showThumbs={false}
					swipeable={true}
					showStatus={false}
					infiniteLoop={true}
					interval={2000}
					showIndicators={false}
					renderArrowPrev={(clickHandler, hasPrev) => hasPrev && <BackArrow onClick={clickHandler} />}
					renderArrowNext={(clickHandler, hasNext) =>
						hasNext && (
							<ForwardArrow
								fontSize="small"
								onClick={clickHandler}
							/>
						)
					}
					preventMovementUntilSwipeScrollTolerance={true}
					swipeScrollTolerance={100}
				>
					{is_user_creator
						? allImages.map((image, index) => (
								<MainContainer
									onClick={() => handleModalOpen()}
									ref={mainContainerRef}
									height={`${containerHeight}px`}
									key={index}
								>
									<ImageSection
										sx={{
											borderRadius:
												ad_text !== "" && router.query.ad_text !== undefined && router.query.ad_text === "true"
													? "16px 16px 0px 0px"
													: "16px",
										}}
										src={image.url as string}
										alt={image.category}
										loading="lazy" //TODO should be eager if index is and fetchpriority missing
										referrerPolicy="no-referrer"
									/>
								</MainContainer>
							))
						: allImages
								.map((image, index) => (
									<MainContainer
										onClick={() => handleModalOpen()}
										ref={mainContainerRef}
										height={`${containerHeight}px`}
										key={index}
									>
										<ImageSection
											sx={{
												borderRadius:
													ad_text !== "" && router.query.ad_text !== undefined && router.query.ad_text === "true"
														? "16px 16px 0px 0px"
														: "16px",
											}}
											src={image.url as string}
											alt={image.category}
											loading="lazy"
											referrerPolicy="no-referrer"
										/>
									</MainContainer>
								))
								.concat(
									<MainContainer
										height={`${containerHeight}px`}
										key="request-more"
										style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
									>
										<SvgIcon
											component={RequestImagesIcon}
											inheritViewBox={false}
											viewBox="0 0 41 33"
											width={24}
											height={24}
											sx={{
												width: "2.563rem",
												height: "1.976rem",
												marginBottom: "0.5rem",
												fill: "none",
											}}
										/>
										<Typography
											variant="subtitle2"
											sx={{
												margin: "0rem 0rem 1.563rem 0rem",
												fontSize: "1rem",
												fontWeight: 500,
												color: "#666666",
												[theme.breakpoints.down("sm")]: {
													fontSize: "13px",
												},
											}}
										>
											{localLang.wantToseeMoreImages}
										</Typography>
										<BeegruButton
											style={{
												padding: "10px 20px",
												fontSize: "1rem",
												fontWeight: 500,
												cursor: "pointer",
												backgroundColor: "#FC801929",
												color: "#B05911",
												borderRadius: "0.5rem",
												border: "none",
												width: "auto",
												[theme.breakpoints.down("sm")]: {
													fontSize: "13px",
												},
											}}
											onClick={(event: any) => {
												event.preventDefault();
												session !== null ? submit(false) : signIn();
											}}
										>
											{localLang.requestImages}
										</BeegruButton>
									</MainContainer>,
								)}
				</CarouselComp>
				{images.length > 1 && (
					<SeemoreContainer
						sx={{
							borderRadius:
								ad_text !== "" && router.query.ad_text !== undefined && router.query.ad_text === "true"
									? "1rem 0rem 0rem 0rem"
									: "1rem 0rem 1rem 0rem",
						}}
						onClick={handleModalOpen}
					>
						<CollectionsOutlinedIcon
							sx={{
								color: "#FFFFFF",
								[theme.breakpoints.down("sm")]: {
									height: "1.5rem",
									width: "1.5rem",
								},
							}}
						/>
						<Typography
							variant="subtitle2"
							sx={{
								color: "#FFFFFF",
								[theme.breakpoints.down("sm")]: {
									fontSize: "0.75rem",
								},
							}}
						>
							{localLang.seeAllImages}
						</Typography>
					</SeemoreContainer>
				)}
			</div>
			{ad_text !== "" && router.query.ad_text !== undefined && router.query.ad_text === "true" && (
				<div
					style={{
						background: " #1BA672",
						padding: "0.75rem 1rem",
						borderRadius: "0rem 0rem 1rem 1rem",
						width: "100%",
						marginTop: "-2px",
						zIndex: 1,
						[theme.breakpoints.down("sm")]: {
							padding: "1.25rem 0.5rem 1.25rem 0.5rem",
						},
					}}
				>
					<Typography
						variant="h6"
						sx={{
							fontSize: "1.25rem",
							fontWeight: 500,
							color: "#FFFFFF",
							lineHeight: "1.5rem",
							[theme.breakpoints.down("sm")]: {
								fontSize: "0.825rem",
								lineHeight: "1rem",
							},
						}}
					>
						{ad_text}
					</Typography>
				</div>
			)}
		</React.Fragment>
	);
};

export default NewPropertyImageComponent;
